/**
 * @description Hook utilizado para manipular os exames solicitados
 * @author Diego Sabino Amorim de Araújo
 * @file useExameSolicitadoService.js
 * @license Copyright (c) 2022 SIGEP DIGETI ITEP/RN
 */

import actionTypes from 'utils/actionTypes'
import { useDispatch } from 'react-redux'
import { useFetch } from './useFetch'

export const useExameSolicitadoService = () => {
  const fetch = useFetch()
  const dispatch = useDispatch()

  const listarExamesDoUsuarioEmAndamentoAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`exameSolicitado/deExaminador/emandamento`)
      return response.data.examesSolicitados
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesDoUsuarioFinalizadosAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`exameSolicitado/deExaminador/finalizados`)
      return response.data.examesSolicitados
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExameSolicitado = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/exameSolicitado/${id}/detalhado`)
      dispatch({
        type: actionTypes.FETCH_EXAME_SOLICITADO,
        exameSolicitado: response.data.exameSolicitado,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExameSolicitadoAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/exameSolicitado/${id}/detalhado`)
      return response.data.exameSolicitado
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesDeSetoresDoUsuario = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get('/exameSolicitado/deSetoresDoUsuario')
      dispatch({
        type: actionTypes.FETCH_EXAMES_SOLICITADOS_DOS_SETORES_DO_USUARIO,
        examesSolicitadosDosSetoresDoUsuario: response.data.examesSolicitados,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesDeSetoresDoUsuarioAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get('/exameSolicitado/deSetoresDoUsuario')
      return response.data.examesSolicitados || []
    } catch (err) {
      return []
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesPendentesDeSetoresDoUsuario = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get('/exameSolicitado/deSetoresDoUsuario/pendentes')
      dispatch({
        type: actionTypes.FETCH_EXAMES_SOLICITADOS_PENDENTES_DOS_SETORES_DO_USUARIO,
        examesSolicitadosPendentesDosSetoresDoUsuario: response.data.examesSolicitados,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesPendentesDeSetoresDoUsuarioAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get('/exameSolicitado/deSetoresDoUsuario/pendentes')
      return response.data.examesSolicitados || []
    } catch (err) {
      return []
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesEmAndamentoDeSetoresDoUsuario = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get('/exameSolicitado/deSetoresDoUsuario/emandamento')
      dispatch({
        type: actionTypes.FETCH_EXAMES_SOLICITADOS_EM_ANDAMENTO_DOS_SETORES_DO_USUARIO,
        examesSolicitadosEmAndamentoDosSetoresDoUsuario: response.data.examesSolicitados,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesEmAndamentoDeSetoresDoUsuarioAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get('/exameSolicitado/deSetoresDoUsuario/emandamento')
      return response.data.examesSolicitados || []
    } catch (err) {
      return []
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesSolicitadosPendentesDeVestigio = async (idVestigio, setExames, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/exameSolicitado/deVestigio/pendentes/${idVestigio}`)
      setExames(response.data.examesSolicitados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const assumirExame = async (id, tipoExame, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.postOpen(`/exameSolicitado/assumir/${id}`, { tipoExame })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const abandonarExame = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.put(`/exameSolicitado/abandonar/${id}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const atribuirExaminadores = async (id, tipoExame, novosExaminadores, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.postOpen(`/exameSolicitado/atribuirExaminadores/${id}`, {
        listaUsuarios: novosExaminadores,
        tipoExame,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const atribuirExaminadoresSuperior = async (id, tipoExame, novosExaminadores, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.postOpen(`/exameSolicitado/atribuirExaminadores/superior/${id}`, {
        listaUsuarios: novosExaminadores,
        tipoExame,
      })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const vincularObjetoDoCaso = async (id, objeto, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      let dados = null

      if (Object.prototype.hasOwnProperty.call(objeto, 'nic')) {
        dados = { idCadaver: objeto.id }
      } else if (Object.prototype.hasOwnProperty.call(objeto, 'placa')) {
        dados = { idVeiculo: objeto.id }
      } else if (Object.prototype.hasOwnProperty.call(objeto, 'cpf')) {
        dados = { idPericiando: objeto.id }
      } else if (Object.prototype.hasOwnProperty.call(objeto, 'cidade')) {
        dados = { idLocal: objeto.id }
      } else if (Object.prototype.hasOwnProperty.call(objeto, 'identificador')) {
        dados = { idVestigio: objeto.id }
      }
      await fetch.putOpen(`/exameSolicitado/vincularObjeto/${id}`, dados)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarQuantidadeExamesSolicitadosPorTipoDeRegionaisAsync = async (setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/exameSolicitado/contar/todasregionais`)
      return response.data.examesSolicitados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarExamesSolicitadosNaoFinalizadosDeVestigioAsync = async (idVestigio, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`exameSolicitado/deVestigio/naoFinalizados/${idVestigio}`)
      return response.data.examesSolicitados
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const cadastrarExameSolicitado = async (values, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.postOpen(`exameSolicitado`, values)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const registrarDownload = async (idExameSolicitado, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.putOpen(`exameSolicitado/registrarDownload/${idExameSolicitado}`)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const editarExameSolicitado = async (idExameSolicitado, values, operacao) => {
    try {
      await fetch.putOpen(`exameSolicitado/editar/${idExameSolicitado}`, { ...values, operacao })
    } catch (err) {
      return
    }
  }

  const alterarStatusExame = async (id, status, reiniciarTexto, processoSEI) => {
    try {
      const dados = {
        status,
        ...(reiniciarTexto === 'SIM' && { versaoDocumento: 0 }),
        processoSEI,
      }
      await fetch.putOpen(`exameSolicitado/alterarStatus/${id}`, dados)
    } catch (err) {
      return
    }
  }

  const assinarExameSolicitado = async (id) => {
    try {
      await fetch.put(`exameSolicitado/assinar/solicitacao/${id}`)
    } catch (err) {
      return null
    } finally {
      return
    }
  }

  const openPDFInNewTab = (url, nome) => {
    window.open(url, 'nome_especifico')
  }

  const downloadPDFDocumentoExameSolicitado = async (id) => {
    try {
      const response = await fetch.getWithOptions(`/exameSolicitado/documento/pdf/${id}`, {
        responseType: 'blob',
      })

      const fileURL = URL.createObjectURL(response.data)

      openPDFInNewTab(fileURL, 'Documento Exame Solicitado')
    } catch (err) {
      throw err
    }
  }

  const editarPrivacidadeExameSolicitado = async (id, isPrivate, justificativaPrivacidade, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      await fetch.putOpen(`/exameSolicitado/privacidade/${id}`, { isPrivate, justificativaPrivacidade })
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  return {
    listarExamesDoUsuarioEmAndamentoAsync,
    listarExamesDoUsuarioFinalizadosAsync,
    listarExameSolicitado,
    listarExamesDeSetoresDoUsuario,
    listarExamesDeSetoresDoUsuarioAsync,
    listarExamesPendentesDeSetoresDoUsuario,
    listarExamesPendentesDeSetoresDoUsuarioAsync,
    listarExamesEmAndamentoDeSetoresDoUsuario,
    listarExamesEmAndamentoDeSetoresDoUsuarioAsync,
    assumirExame,
    abandonarExame,
    atribuirExaminadores,
    atribuirExaminadoresSuperior,
    listarExamesSolicitadosPendentesDeVestigio,
    vincularObjetoDoCaso,
    listarQuantidadeExamesSolicitadosPorTipoDeRegionaisAsync,
    listarExamesSolicitadosNaoFinalizadosDeVestigioAsync,
    cadastrarExameSolicitado,
    registrarDownload,
    listarExameSolicitadoAsync,
    editarExameSolicitado,
    alterarStatusExame,
    assinarExameSolicitado,
    downloadPDFDocumentoExameSolicitado,
    editarPrivacidadeExameSolicitado,
  }
}
